import React, { useContext, useEffect, useMemo, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import Cookies from "js-cookie";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Container from "@mui/material/Container";
import IconButton from "@mui/material/IconButton";
import ListItem from "@mui/material/ListItem";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";

import SVG from "@/assets/svg";
import { HeaderStyled } from "@/component/header/headerStyled";
import { OPTIONS, OptionsType } from "@/component/header/options";
import {
  ALERT_FLAG,
  FAMILY_ADMIN_INTERFACE_FLAG,
  FINANCE_INVOICES_FLAG,
  ROSTER_LOCATION_MISMATCH_FLAG,
  SETTINGS_ACCESS_RESTRICTIONS_FLAG,
  SETTINGS_VISIT_DISCREPANCY_FLAG,
} from "@/config/releaseFlags";
import { SocketContext } from "@/context";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { $public } from "@/services";
import { logOutUser } from "@/services/auth";
import { ACCESS_LEVEL, TEMP_AVATAR } from "@/utils/constants";
import { convertCamelCaseToSentenceCase, isNotSSR } from "@/utils/helpers";
import { useFeatureFlags } from "@/utils/providers/feature-flags-provider";
import { isSupportMode } from "@/utils/support-access";
import MobileHeader from "./mobile-header";
import { accessibleInteractiveColors } from "@/theme/accessibility";
import AnnouncementBanner from "@/component/announcement-banner";
import logger from "@/runtime-utils/observability/logger";

const SERVICE_TABS_SCROLL_TOLERANCE = 8;

function Header() {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const openDropDown = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const [open, setOpen] = React.useState(false);
  const [show, setShow] = React.useState(false);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const handleOpen = () => setOpen(!open);
  const handleSettingsOpen = () => setSettingsOpen(!settingsOpen);

  // State for Initial Assessment dropdown
  const [initialAssessmentOpen, setInitialAssessmentOpen] = useState<
    string | null
  >(null);
  const [submenuAnchorEl, setSubmenuAnchorEl] = useState<HTMLElement | null>(
    null
  );

  const router = useRouter();

  // State for Finance top-nav dropdown
  const [financeDropdownOpen, setFinanceDropdownOpen] = useState(false);
  const [financeDropdownAnchorEl, setFinanceDropdownAnchorEl] =
    useState<HTMLElement | null>(null);

  const toggleFinanceDropdown = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    setFinanceDropdownOpen((prev) => !prev);
    setFinanceDropdownAnchorEl(event.currentTarget);
  };

  const handleFinanceDropdownClose = () => {
    setFinanceDropdownOpen(false);
    setFinanceDropdownAnchorEl(null);
  };

  const isFinanceRoute = (path: string) => router.pathname.startsWith(path);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setFinanceDropdownOpen(false);
    setFinanceDropdownAnchorEl(null);
  }, [router.pathname]);

  const dispatch = useAppDispatch();
  const { isUnreadMsgs, familyUnreadCount = 0 } = useContext(SocketContext);
  const { user } = useAppSelector((state) => state.user);
  const { terms, tenantSubscription, tenantSubscriptionLoaded } =
    useAppSelector((state) => state.terms);
  const { isEnabled } = useFeatureFlags();
  const { pathname } = router;
  const MONGO_ID_REGEX = /^[0-9a-f]{24}$/i;
  const [supportMode, setSupportMode] = useState(false);
  const sessionAccessLevel = useMemo(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    try {
      const token = Cookies.get("token");
      const payload = token?.split(".")[1];
      if (!payload) {
        return undefined;
      }

      const normalizedPayload = payload
        .replace(/-/g, "+")
        .replace(/_/g, "/")
        .padEnd(payload.length + ((4 - (payload.length % 4)) % 4), "=");
      return JSON.parse(window.atob(normalizedPayload)).accessLevel as
        ACCESS_LEVEL | undefined;
    } catch {
      return undefined;
    }
  }, []);
  // Derive labels during render so persisted terms are visible immediately
  // after Redux rehydration, without a generic-label flash from an effect.
  const newOptions = useMemo(
    () =>
      OPTIONS.map((item) => {
        if (item.id === "service-user") {
          return { ...item, title: terms.data?.serviceUser || item.title };
        }
        if (item.id === "staff") {
          return { ...item, title: terms.data?.staff || item.title };
        }
        return item;
      }),
    [terms.data?.serviceUser, terms.data?.staff]
  );
  const menuOptions = newOptions;
  const selectedOptionForRoute = useMemo(
    () =>
      newOptions.find((option) => router.pathname.includes(option.path)) ??
      null,
    [newOptions, router.pathname]
  );

  useEffect(() => {
    setSupportMode(isSupportMode());
  }, [pathname]);

  const allowedPages = useMemo(() => {
    if (!tenantSubscription)
      return ["staff", "service", "service/incompatibility"];
    const allowedPages =
      tenantSubscription?.currentSub?.plan?.allowedPages || [];
    logger.debug("Admin allowedPages from subscription:", allowedPages);
    logger.debug(
      "Reports in admin allowedPages:",
      allowedPages.includes("reports")
    );
    const finalPages = [
      "staff",
      "service",
      "staff/staff-members",
      "staff/admin-notes",
      "service/service-user",
      "service/incompatibility",
      // "finance",
      "finance/invoices",
      "finance/invoice-rates",
      "finance/invoice-groups",
      "finance/invoice-template",
      // "alert",
      // "reports",
      ...allowedPages,
    ];
    logger.debug("Final allowedPages:", finalPages);
    return finalPages;
  }, [tenantSubscription]);

  const [currentSelectedOption, setCurrentSelectedOption] =
    useState<OptionsType | null>(null);
  const selectedOption = currentSelectedOption ?? selectedOptionForRoute;
  const [currentSelectedChild, setCurrentSelectedChild] = useState<OptionsType>(
    {
      id: "",
      title: "",
      path: "",
    }
  );
  const [currentSelectedSubChild, setCurrentSelectedSubChild] =
    useState<OptionsType | null>(null);
  const tabsBarRef = React.useRef<HTMLDivElement>(null);
  const [serviceTabsOverflow, setServiceTabsOverflow] = useState(false);
  const [serviceTabsScrollState, setServiceTabsScrollState] = useState({
    canScrollLeft: false,
    canScrollRight: false,
  });
  const updateServiceTabsScrollState = React.useCallback(() => {
    const tabsBar = tabsBarRef.current;
    if (!tabsBar) {
      setServiceTabsOverflow(false);
      setServiceTabsScrollState({
        canScrollLeft: false,
        canScrollRight: false,
      });
      return;
    }

    const remainingScrollDistance =
      tabsBar.scrollWidth - tabsBar.clientWidth - tabsBar.scrollLeft;
    const hasOverflow =
      tabsBar.scrollWidth - tabsBar.clientWidth > SERVICE_TABS_SCROLL_TOLERANCE;
    setServiceTabsOverflow(hasOverflow);
    setServiceTabsScrollState({
      canScrollLeft:
        hasOverflow && tabsBar.scrollLeft > SERVICE_TABS_SCROLL_TOLERANCE,
      canScrollRight:
        hasOverflow && remainingScrollDistance > SERVICE_TABS_SCROLL_TOLERANCE,
    });
  }, []);
  const scrollServiceTabs = (left: number) => {
    tabsBarRef.current?.scrollBy({ behavior: "smooth", left });
  };
  const scrollSelectedServiceTab = React.useCallback(
    (tab: HTMLAnchorElement | null) => {
      if (!tab) {
        return;
      }

      const tabsBar = tab.closest<HTMLDivElement>(".tabsbar");
      if (!tabsBar) {
        return;
      }

      const tabBarBounds = tabsBar.getBoundingClientRect();
      const tabBounds = tab.getBoundingClientRect();
      const targetScrollLeft =
        tabsBar.scrollLeft +
        tabBounds.left -
        tabBarBounds.left -
        (tabsBar.clientWidth - tabBounds.width) / 2;

      if (typeof tabsBar.scrollTo === "function") {
        tabsBar.scrollTo({ left: Math.max(0, targetScrollLeft) });
      } else {
        tabsBar.scrollLeft = Math.max(0, targetScrollLeft);
      }
    },
    []
  );

  const handleSubOptions = (option: OptionsType) => {
    setCurrentSelectedOption(option);
  };

  useEffect(() => {
    const selectedOption = newOptions.find((option) =>
      router.pathname.includes(option.path)
    );
    if (selectedOption) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCurrentSelectedOption(selectedOption);
      if (selectedOption.child) {
        const selectedChild = selectedOption.child.findLast((child) =>
          router.pathname.includes(child.path)
        );
        if (
          // make the subheader active only when a user is selected
          (router.pathname.includes("staff-members") ||
            router.pathname.includes("service-user")) &&
          !router.pathname.split("/")?.[3]
        ) {
          return;
        }
        if (selectedChild) {
          setCurrentSelectedChild(selectedChild);

          if (selectedChild.child) {
            const selectedSubChild = selectedChild.child.findLast((subChild) =>
              router.pathname.includes(subChild.path)
            );
            if (selectedSubChild) {
              setCurrentSelectedSubChild(selectedSubChild);
            } else {
              setCurrentSelectedSubChild(null);
            }
          } else {
            setCurrentSelectedSubChild(null);
          }
        }
      }
    }
  }, [newOptions, router]);

  const hideHeading =
    pathname === "/dashboard" ||
    pathname === "/chat" ||
    pathname.includes("profile") ||
    (pathname.includes("initial-assessment") &&
      (pathname.includes("initial-detail") || pathname.includes("edit"))) ||
    (pathname.includes("risk-assessments") &&
      (pathname.includes("edit") ||
        pathname.includes("costume-risk") ||
        pathname.includes("history") ||
        pathname.includes("view"))) ||
    pathname.includes("custom-title") ||
    pathname.includes("add-risk") ||
    (pathname.includes("/medication/") &&
      (pathname.includes("medication-list") ||
        pathname.includes("mar-chart") ||
        pathname.includes("medication-details") ||
        pathname.includes("discontinued-medication"))) ||
    (pathname.includes("body-map") &&
      (pathname.includes("detail") ||
        pathname.includes("description") ||
        pathname.includes("edit"))) ||
    (pathname.includes("support-plan") &&
      (pathname.includes("initial-detail") ||
        pathname.includes("support-detail") ||
        pathname.includes("view-history") ||
        pathname.includes("history") ||
        pathname.includes("edit"))) ||
    pathname.includes("report");

  useEffect(() => {
    const tabsBar = tabsBarRef.current;
    if (!tabsBar) {
      updateServiceTabsScrollState();
      return;
    }

    const updateOverflow = () => {
      updateServiceTabsScrollState();
    };
    let animationFrame = 0;
    const handleResizeObserver: ResizeObserverCallback = () => {
      // Defer the state update until layout has settled. Updating synchronously
      // from ResizeObserver can trigger the browser's loop-limit warning.
      cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(updateOverflow);
    };

    updateOverflow();
    const observer = new ResizeObserver(handleResizeObserver);
    observer.observe(tabsBar);
    window.addEventListener("resize", updateOverflow);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(animationFrame);
      window.removeEventListener("resize", updateOverflow);
    };
  }, [currentSelectedOption, hideHeading, updateServiceTabsScrollState]);

  const hasPermission = (option: OptionsType) => {
    return option?.authority?.length > 0
      ? Boolean(
          (user.accessLevel || sessionAccessLevel) &&
          option.authority.includes(
            (user.accessLevel || sessionAccessLevel) as ACCESS_LEVEL
          )
        )
      : true;
  };

  const isOptionEnabled = (option: OptionsType) => {
    return option.featureFlagKey
      ? isEnabled(option.featureFlagKey, false)
      : true;
  };

  const normalizeMenuPath = (path: string) => path.replace(/^\/+/, "");

  const isMenuOptionVisible = (option: OptionsType, fullPath: string) => {
    const isFlagVisible = option.featureFlagKey
      ? isOptionEnabled(option)
      : true;

    if (!isFlagVisible) {
      return false;
    }

    if (!tenantSubscriptionLoaded) {
      return true;
    }

    const isAllowed = allowedPages?.includes(
      normalizeMenuPath(option.subscriptionPath ?? fullPath)
    );

    // if (fullPath.includes("finance")) {
    //   isAllowed = true;
    // }

    if (option.featureFlagKey) {
      return isOptionEnabled(option) && isAllowed;
    }

    return isAllowed;
  };

  const handleLogout = async () => {
    try {
      dispatch({ type: "RESET" });
      await logOutUser();
      await router.push("/sign-in");
    } catch {
      if (isNotSSR) {
        window.location.reload();
      }
    }
  };

  // Toggle Initial Assessment dropdown
  const toggleInitialAssessmentDropdown = (
    optionId: string,
    anchor: HTMLElement
  ) => {
    const isClosing = initialAssessmentOpen === optionId;
    setInitialAssessmentOpen(isClosing ? null : optionId);
    setSubmenuAnchorEl(isClosing ? null : anchor);
  };

  // Handle dropdown option click
  const handleDropdownOptionClick = (
    parentPath: string,
    childPath: string,
    subChildPath: string
  ) => {
    router.push(`${parentPath}${childPath}${subChildPath}`);
    setInitialAssessmentOpen(null);
  };

  const getBodyMapBasePath = () => {
    const queryUserId = Array.isArray(router.query.userId)
      ? router.query.userId[0]
      : router.query.userId;

    if (queryUserId && MONGO_ID_REGEX.test(queryUserId)) {
      return `/service/body-map/${queryUserId}`;
    }

    const pathId = router.asPath
      .split(/[?#]/)[0]
      .split("/")
      .find((segment) => MONGO_ID_REGEX.test(segment));

    return pathId ? `/service/body-map/${pathId}` : "/service/body-map";
  };

  const handleBodyMapBack = () => {
    if (pathname.includes("edit")) {
      router.back();
      return;
    }

    router.push(getBodyMapBasePath());
  };

  const checkAllowedPages = (option: OptionsType) => {
    const getNestedChildrenPaths = (option: OptionsType): OptionsType[] => {
      const paths: OptionsType[] = [];
      if (option.child) {
        option.child.forEach((child) => {
          const childPath = option.path + child.path;

          if (child.child) {
            const visibleChildren = child.child
              .map((subChild) => ({
                ...subChild,
                path: normalizeMenuPath(childPath + subChild.path),
              }))
              .filter((subChild) =>
                isMenuOptionVisible(subChild, subChild.path)
              );

            if (visibleChildren.length > 0) {
              paths.push({
                ...child,
                child: visibleChildren,
              });
            }
          } else {
            const fullPath = normalizeMenuPath(childPath);

            if (isMenuOptionVisible(child, fullPath)) {
              paths.push({
                ...child,
                path: fullPath,
              });
            }
          }
        });
      }
      return paths;
    };

    return getNestedChildrenPaths(option);
  };

  return (
    <HeaderStyled
      style={{ position: "sticky", top: 0, width: "100%", zIndex: 999 }}
    >
      <AnnouncementBanner />
      <Box
        className="header"
        style={{
          background: " #EAF6FF",
          color: accessibleInteractiveColors.navigationBlue,
          width: "100%",
          fontFamily: "Poppins",
        }}
      >
        <Box className="mainHeaderLayout">
          <Box className="Navbar">
            <Toolbar
              className="nav"
              disableGutters
              sx={{
                justifyContent: "space-between",
              }}
            >
              <Typography
                className="logo"
                variant="h6"
                noWrap
                component="a"
                id="app-logo"
                onClick={() => router.push("/")}
                sx={{
                  mr: 2,
                  display: { xs: "none", md: "flex" },
                  fontWeight: 700,
                  letterSpacing: ".3rem",
                  textDecoration: "none",
                  color: "red",
                  marginRight: "0",
                  padding: "0",
                }}
              >
                <SVG.Logo width="141px" />
              </Typography>

              <Box
                sx={{ display: "flex", gap: "5px" }}
                className="web-header-layout"
              >
                {menuOptions
                  .filter(
                    (item) =>
                      item.shouldShow && isMenuOptionVisible(item, item.path)
                  )
                  .map((option) => {
                    // Finance gets a special dropdown (Invoice / Mileage)
                    if (option.id === "finance") {
                      const isFinanceSelected =
                        selectedOption?.id === option.id;
                      return (
                        <Box key={option.id} sx={{ position: "relative" }}>
                          <Button
                            onClick={(e) => {
                              handleSubOptions(option);
                              toggleFinanceDropdown(e);
                            }}
                            className="headerButton"
                            aria-haspopup="menu"
                            aria-expanded={financeDropdownOpen}
                            sx={[
                              {
                                my: 1,
                                display: "flex",
                                alignItems: "center",
                                gap: "2px",
                                textTransform: "none",
                              },
                              isFinanceSelected
                                ? { color: "#651F70" }
                                : {
                                    color:
                                      accessibleInteractiveColors.navigationBlue,
                                  },
                            ]}
                          >
                            <Box
                              sx={{
                                whiteSpace: "nowrap",
                                fontFamily: "Poppins",
                                fontSize: 15,
                              }}
                            >
                              {option.title}
                            </Box>
                            {financeDropdownOpen ? (
                              <ExpandLess
                                sx={{ fontSize: 18, marginRight: "-10px" }}
                              />
                            ) : (
                              <ExpandMore
                                sx={{ fontSize: 18, marginRight: "-10px" }}
                              />
                            )}
                          </Button>
                          <Popper
                            id="finance-top-nav-menu"
                            open={financeDropdownOpen}
                            anchorEl={financeDropdownAnchorEl}
                            placement="bottom-start"
                            sx={{ zIndex: 9999 }}
                            modifiers={[
                              {
                                name: "offset",
                                options: { offset: [0, 8] },
                              },
                              {
                                name: "preventOverflow",
                                options: { padding: 8 },
                              },
                            ]}
                          >
                            {financeDropdownOpen && (
                              <ClickAwayListener
                                onClickAway={handleFinanceDropdownClose}
                              >
                                <Paper
                                  role="menu"
                                  sx={{
                                    minWidth: "200px",
                                    overflow: "hidden",
                                  }}
                                >
                                  <Link
                                    href="/finance/invoices"
                                    style={{ textDecoration: "none" }}
                                    onClick={handleFinanceDropdownClose}
                                  >
                                    <Box
                                      role="menuitem"
                                      sx={[
                                        {
                                          padding: "10px 16px",
                                          cursor: "pointer",
                                          fontWeight: 400,
                                          borderBottom: "1px solid #eee",
                                          fontSize: "14px",
                                          color: "#3E73A4",
                                          fontFamily: "Poppins",
                                          "&:hover": {
                                            backgroundColor: "#f5f5f5",
                                          },
                                        },
                                        isFinanceRoute("/finance/invoice") && {
                                          color: "#651F70",
                                          backgroundColor: "#f5f5f5",
                                        },
                                      ]}
                                    >
                                      Invoice
                                    </Box>
                                  </Link>
                                  <Link
                                    href="/finance/mileage"
                                    style={{ textDecoration: "none" }}
                                    onClick={handleFinanceDropdownClose}
                                  >
                                    <Box
                                      role="menuitem"
                                      sx={[
                                        {
                                          padding: "10px 16px",
                                          cursor: "pointer",
                                          fontWeight: 400,
                                          fontSize: "14px",
                                          color: "#3E73A4",
                                          fontFamily: "Poppins",
                                          "&:hover": {
                                            backgroundColor: "#f5f5f5",
                                          },
                                        },
                                        isFinanceRoute("/finance/mileage") && {
                                          color: "#651F70",
                                          backgroundColor: "#f5f5f5",
                                        },
                                      ]}
                                    >
                                      Mileage
                                    </Box>
                                  </Link>
                                </Paper>
                              </ClickAwayListener>
                            )}
                          </Popper>
                        </Box>
                      );
                    }

                    // All other nav items render as normal links
                    return (
                      <Link
                        key={option.id}
                        href={option.path}
                        style={{
                          color:
                            selectedOption?.id === option.id
                              ? "#651F70"
                              : accessibleInteractiveColors.navigationBlue,
                        }}
                      >
                        <Button
                          onClick={() => handleSubOptions(option)}
                          className="headerButton"
                          sx={[
                            {
                              my: 1,
                              display: "block",
                              textTransform: "none",
                            },
                            selectedOption?.id === option.id
                              ? {
                                  color: "#651F70",
                                }
                              : {
                                  color:
                                    accessibleInteractiveColors.navigationBlue,
                                },
                          ]}
                        >
                          <Box
                            sx={{
                              whiteSpace: "nowrap",
                              fontFamily: "Poppins",
                              fontSize: 15,
                            }}
                          >
                            {convertCamelCaseToSentenceCase(option.title)}
                          </Box>
                        </Button>
                      </Link>
                    );
                  })}
              </Box>
              <Box className="mobile-header-layout">
                <MobileHeader />{" "}
              </Box>
              <Box
                className="header-actions"
                sx={{
                  display: "flex",
                  gap: "1.125rem",
                  alignItems: "center",
                  svg: {
                    cursor: "pointer",
                  },
                }}
              >
                <Link href="/chat" aria-label="Messages">
                  <Box sx={{ display: "flex" }}>
                    <Badge
                      overlap="circular"
                      badgeContent={isUnreadMsgs.length + familyUnreadCount}
                      color="primary"
                    >
                      <SVG.Msg />
                    </Badge>
                  </Box>
                </Link>
                <SVG.Setting onClick={handleSettingsOpen} />
                <Box sx={{ position: "relative" }}>
                  <Box
                    onClick={handleOpen}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      cursor: "pointer",
                    }}
                  >
                    {user?.avatar ? (
                      <Image
                        alt="avatar"
                        src={$public(user?.avatar) as string}
                        width={40}
                        height={40}
                        style={{
                          borderRadius: "50%",
                        }}
                      />
                    ) : (
                      <Image
                        width={40}
                        height={40}
                        src={TEMP_AVATAR}
                        alt=""
                        style={{
                          borderRadius: "50%",
                        }}
                      />
                    )}
                    <SVG.DownArrow />
                  </Box>
                  {settingsOpen && (
                    <ClickAwayListener
                      onClickAway={() => setSettingsOpen(false)}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          background: "#fff",
                          width: "225px",
                          left: "-156px",
                          padding: "8px 0px",
                          borderRadius: "8px",
                          zIndex: "999",
                          boxShadow: "0px 0px 7px #ccc",
                          cursor: "pointer",
                        }}
                      >
                        <Box>
                          <Link href={"/Zones/zone"}>
                            <ListItem sx={{ color: "rgb(62, 115, 164)" }}>
                              Zones
                            </ListItem>
                          </Link>
                        </Box>
                        {[ACCESS_LEVEL.ADMINISTRATOR].includes(
                          user.accessLevel
                        ) && (
                          <Box>
                            <Link href={"/profile/terms"}>
                              <ListItem sx={{ color: "rgb(62, 115, 164)" }}>
                                Language preferences
                              </ListItem>
                            </Link>
                          </Box>
                        )}
                        {[ACCESS_LEVEL.ADMINISTRATOR].includes(
                          user.accessLevel
                        ) && (
                          <Box>
                            {isEnabled(
                              ROSTER_LOCATION_MISMATCH_FLAG,
                              false
                            ) && (
                              <Link href={"/profile/toggle-alerts"}>
                                <ListItem sx={{ color: "rgb(62, 115, 164)" }}>
                                  Roster
                                </ListItem>
                              </Link>
                            )}
                          </Box>
                        )}
                        {[ACCESS_LEVEL.ADMINISTRATOR].includes(
                          user.accessLevel
                        ) && (
                          <Box>
                            {isEnabled(
                              SETTINGS_ACCESS_RESTRICTIONS_FLAG,
                              false
                            ) && (
                              <Link href={"/profile/access-restrictions"}>
                                <ListItem sx={{ color: "rgb(62, 115, 164)" }}>
                                  Access Restrictions
                                </ListItem>
                              </Link>
                            )}
                          </Box>
                        )}
                        {[
                          ACCESS_LEVEL.ADMINISTRATOR,
                          ACCESS_LEVEL.SENIOR_USER,
                        ].includes(user.accessLevel) &&
                        tenantSubscriptionLoaded &&
                        allowedPages.includes("service/family-access") &&
                        isEnabled(FAMILY_ADMIN_INTERFACE_FLAG, false) ? (
                          <Link href="/profile/family-app">
                            <ListItem sx={{ color: "rgb(62, 115, 164)" }}>
                              Family App
                            </ListItem>
                          </Link>
                        ) : null}
                        {[ACCESS_LEVEL.ADMINISTRATOR].includes(
                          user.accessLevel
                        ) && (
                          <Box>
                            {isEnabled(
                              SETTINGS_VISIT_DISCREPANCY_FLAG,
                              false
                            ) && (
                              <Link href={"/profile/visit-discrepancy"}>
                                <ListItem sx={{ color: "rgb(62, 115, 164)" }}>
                                  Visit Discrepancy
                                </ListItem>
                              </Link>
                            )}
                          </Box>
                        )}
                        {[ACCESS_LEVEL.ADMINISTRATOR].includes(
                          user.accessLevel
                        ) && (
                          <Box>
                            {isEnabled(ALERT_FLAG, false) && (
                              <Link href={"/profile/alert"}>
                                <ListItem sx={{ color: "rgb(62, 115, 164)" }}>
                                  Alerts
                                </ListItem>
                              </Link>
                            )}
                          </Box>
                        )}
                      </Box>
                    </ClickAwayListener>
                  )}
                  {open && (
                    <ClickAwayListener onClickAway={() => setOpen(false)}>
                      <Box
                        sx={{
                          position: "absolute",
                          background: "#fff",
                          width: "229px",
                          left: "-140px",
                          padding: "12px 1px",
                          borderRadius: "8px",
                          zIndex: "999",
                          boxShadow: "0px 0px 7px #ccc",
                          cursor: "pointer",
                        }}
                      >
                        {!supportMode && (
                          <Box>
                            <Link href={"/profile"}>
                              <ListItem sx={{ color: "rgb(62, 115, 164)" }}>
                                My Profile
                              </ListItem>
                            </Link>
                          </Box>
                        )}
                        <Box>
                          <ListItem
                            sx={{ color: "rgb(62, 115, 164)" }}
                            onClick={() => setShow(!show)}
                          >
                            <Box className="helpSupport">
                              Help and support
                              <SVG.DownArrow sx={{ width: "10px" }} />
                            </Box>
                          </ListItem>
                          {show && (
                            <>
                              <Box className="help">
                                Help-center Coming Soon
                              </Box>
                            </>
                          )}
                        </Box>
                        <Box onClick={handleLogout}>
                          <ListItem>Logout</ListItem>
                        </Box>
                      </Box>
                    </ClickAwayListener>
                  )}
                </Box>
              </Box>
            </Toolbar>
          </Box>
        </Box>
        <Box
          sx={{
            background: "#fff",
            zIndex: "998",
            width: "100%",
            position: "relative",
          }}
        >
          <Box
            className="tabsbar-shell"
            sx={{
              position: "relative",
              width: "100%",
              "&:hover .tabsbar-controls, &:focus-within .tabsbar-controls": {
                opacity: 1,
                pointerEvents: "auto",
              },
            }}
          >
            <Box
              component="nav"
              className="tabsbar"
              id="service-user-navigation"
              ref={tabsBarRef}
              aria-label="Service user navigation"
              onScroll={updateServiceTabsScrollState}
              sx={{
                width: "100%",
                maxWidth: "none",
                margin: "0 auto",
                display: "flex",
                overflowX: serviceTabsOverflow ? "auto" : "hidden",
                overflowY: "hidden",
                boxSizing: "border-box",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              {selectedOption?.child
                ? checkAllowedPages(selectedOption)
                    .filter((option) => isOptionEnabled(option))
                    .map((option) => {
                      if (option.child && option.child.length > 0) {
                        return (
                          <Container
                            key={option.id}
                            className="HeaderOption"
                            sx={{ position: "relative" }}
                          >
                            {!hideHeading && hasPermission(option) && (
                              <Box className="subheader">
                                <Box key={option.id} className="optionData">
                                  <Box
                                    component="button"
                                    type="button"
                                    aria-haspopup="menu"
                                    aria-expanded={
                                      initialAssessmentOpen === option.id
                                    }
                                    aria-controls={
                                      initialAssessmentOpen === option.id
                                        ? `service-nav-menu-${option.id}`
                                        : undefined
                                    }
                                    onClick={(event) =>
                                      toggleInitialAssessmentDropdown(
                                        option.id,
                                        event.currentTarget
                                      )
                                    }
                                    sx={[
                                      {
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "4px",
                                        border: 0,
                                        background: "transparent",
                                        padding: 0,
                                        fontWeight: 500,
                                        fontSize: "14px",
                                        lineHeight: "20px",
                                        fontFamily: "Poppins",
                                        whiteSpace: "nowrap",
                                        textTransform: "capitalize",
                                        color: "#3E73A4",
                                      },
                                      currentSelectedChild.id === option.id && {
                                        color: "#651F70",
                                      },
                                    ]}
                                  >
                                    {option.title}
                                    {initialAssessmentOpen === option.id ? (
                                      <ExpandLess />
                                    ) : (
                                      <ExpandMore />
                                    )}
                                  </Box>

                                  <Popper
                                    id={`service-nav-menu-${option.id}`}
                                    open={initialAssessmentOpen === option.id}
                                    anchorEl={submenuAnchorEl}
                                    placement="bottom-start"
                                    sx={{ zIndex: 9999 }}
                                    modifiers={[
                                      {
                                        name: "offset",
                                        options: { offset: [0, 8] },
                                      },
                                      {
                                        name: "preventOverflow",
                                        options: { padding: 8 },
                                      },
                                    ]}
                                  >
                                    <ClickAwayListener
                                      onClickAway={() => {
                                        setInitialAssessmentOpen(null);
                                        setSubmenuAnchorEl(null);
                                      }}
                                    >
                                      <Paper
                                        role="menu"
                                        sx={{
                                          minWidth: "200px",
                                          overflow: "hidden",
                                        }}
                                      >
                                        {option.child
                                          .filter((subOption) =>
                                            isMenuOptionVisible(
                                              subOption,
                                              subOption.path
                                            )
                                          )
                                          .map((subOption) => {
                                            const { userId } =
                                              router.query ?? {};
                                            let targetPath = `/${subOption.path}`;

                                            if (
                                              userId &&
                                              (subOption.path.includes(
                                                "initial-assessment"
                                              ) ||
                                                subOption.path.includes(
                                                  "support-plan"
                                                ) ||
                                                subOption.path.includes(
                                                  "risk-assessments"
                                                ) ||
                                                subOption.path.includes(
                                                  "family-access"
                                                ))
                                            ) {
                                              targetPath = `/${subOption.path}/${userId}`;
                                            }

                                            return (
                                              <Link
                                                href={targetPath}
                                                key={subOption.id}
                                              >
                                                <Box
                                                  role="menuitem"
                                                  sx={[
                                                    {
                                                      padding: "10px 16px",
                                                      cursor: "pointer",
                                                      fontWeight: 400,
                                                      borderBottom:
                                                        "1px solid #eee",
                                                      fontSize: "14px",
                                                      color: "#3E73A4",
                                                      "&:hover": {
                                                        backgroundColor:
                                                          "#f5f5f5",
                                                      },
                                                    },
                                                    currentSelectedSubChild?.id ===
                                                      subOption.id && {
                                                      color: "#651F70",
                                                      backgroundColor:
                                                        "#f5f5f5",
                                                    },
                                                  ]}
                                                >
                                                  {subOption.title}
                                                </Box>
                                              </Link>
                                            );
                                          })}
                                      </Paper>
                                    </ClickAwayListener>
                                  </Popper>
                                </Box>
                              </Box>
                            )}
                          </Container>
                        );
                      }

                      return (
                        <Container key={option.id} className="HeaderOption">
                          {!hideHeading && hasPermission(option) && (
                            <Box className="subheader">
                              <Box key={option.id} className="optionData">
                                <Link
                                  ref={
                                    currentSelectedChild.id === option.id
                                      ? scrollSelectedServiceTab
                                      : undefined
                                  }
                                  href={`/${option.path}`}
                                  style={{
                                    color:
                                      currentSelectedChild.id === option.id
                                        ? "#651F70"
                                        : "#3E73A4",
                                    borderBottom:
                                      currentSelectedChild.id === option.id
                                        ? "2px solid #651F70"
                                        : "",
                                    paddingBottom:
                                      currentSelectedChild.id === option.id
                                        ? "5px"
                                        : "",
                                  }}
                                >
                                  {option.title}
                                </Link>
                              </Box>
                            </Box>
                          )}
                        </Container>
                      );
                    })
                : ""}
            </Box>
            {serviceTabsOverflow && (
              <Box
                aria-label="Scroll service user navigation"
                className="tabsbar-controls"
                role="group"
                sx={{
                  display: "flex",
                  gap: 0.5,
                  opacity: 0,
                  pointerEvents: "none",
                  position: "absolute",
                  right: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  transition: "opacity 160ms ease-in-out",
                  zIndex: 1,
                  "& .MuiIconButton-root": {
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #3E76C3",
                    borderRadius: "8px",
                    color: "#1F4970",
                    height: 36,
                    width: 36,
                    "&:hover": { backgroundColor: "#EAF6FF" },
                    "&.Mui-disabled": {
                      backgroundColor: "#F2F6FA",
                      borderColor: "#DFE5EF",
                      color: "#7C8FAC",
                    },
                  },
                }}
              >
                <IconButton
                  aria-controls="service-user-navigation"
                  aria-label="Scroll service user navigation left"
                  disabled={!serviceTabsScrollState.canScrollLeft}
                  onClick={() => scrollServiceTabs(-160)}
                  size="small"
                >
                  <KeyboardArrowLeft />
                </IconButton>
                <IconButton
                  aria-controls="service-user-navigation"
                  aria-label="Scroll service user navigation right"
                  disabled={!serviceTabsScrollState.canScrollRight}
                  onClick={() => scrollServiceTabs(160)}
                  size="small"
                >
                  <KeyboardArrowRight />
                </IconButton>
              </Box>
            )}
          </Box>

          <Box sx={{ width: "100%", maxWidth: "1285px", margin: "0 auto" }}>
            {pathname.includes("initial-assessment") &&
              (pathname.includes("initial-detail") ||
                pathname.includes("edit")) && (
                <Box
                  sx={{
                    display: "flex",
                    background: "#fff",
                    zIndex: "998",
                    paddingLeft: "1rem",
                  }}
                >
                  <Box className="optionData" style={{ width: "fit-content" }}>
                    <Box onClick={() => router.back()} className="bodymap-back">
                      <SVG.BackIcon width="20" />
                      {pathname.includes("edit") ? "Back" : "Back"}
                    </Box>
                  </Box>
                </Box>
              )}
          </Box>

          {/* <Box sx={{ width: "1285px", margin: "0 auto" }}>
            {pathname.includes("risk-assessments") &&
              (pathname.includes("edit") || pathname.includes("costume-risk") || pathname.includes("edit")) || pathname.includes("custom-title") && (
                <Box
                  sx={{
                    display: "flex",
                    background: "#fff",
                    zIndex: 998,
                    pl: 2,
                  }}
                >
                  <Box className="optionData" sx={{ width: "fit-content" }}>
                    <Box
                      onClick={() => router.back()}
                      className="bodymap-back"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        cursor: "pointer",
                      }}
                    >
                      <SVG.BackIcon width="20" />
                      Back
                    </Box>
                  </Box>
                </Box>
              )}
          </Box> */}

          <Box sx={{ width: "100%", maxWidth: "1285px", margin: "0 auto" }}>
            {(pathname.includes("risk-assessments") &&
              (pathname.includes("edit") ||
                pathname.includes("costume-risk") ||
                pathname.includes("history") ||
                pathname.includes("view"))) ||
            pathname.includes("custom-title") ||
            pathname.includes("add-risk") ? (
              <Box
                sx={{
                  display: "flex",
                  background: "#fff",
                  zIndex: 998,
                  pl: 2,
                }}
              >
                <Box className="optionData" sx={{ width: "fit-content" }}>
                  <Box
                    onClick={() => router.back()}
                    className="bodymap-back"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      cursor: "pointer",
                    }}
                  >
                    <SVG.BackIcon width="20" />
                    Back
                  </Box>
                </Box>
              </Box>
            ) : null}
          </Box>

          <Box sx={{ width: "100%", maxWidth: "1250px", margin: "0 auto" }}>
            {pathname.includes("support-plan") &&
              (pathname.includes("initial-detail") ||
                pathname.includes("support-detail") ||
                pathname.includes("view-history") ||
                pathname.includes("history") ||
                pathname.includes("view") ||
                pathname.includes("edit")) && (
                <Box
                  sx={{
                    display: "flex",
                    background: "#fff",
                    zIndex: "998",
                    paddingLeft: "1rem",
                  }}
                >
                  <Box className="optionData" style={{ width: "fit-content" }}>
                    <Box onClick={() => router.back()} className="bodymap-back">
                      <SVG.BackIcon width="20" />
                      {pathname.includes(
                        "/service/care-planning/support-plan/history"
                      )
                        ? "History"
                        : "Back"}
                    </Box>
                  </Box>
                </Box>
              )}
          </Box>

          <Box sx={{ width: "100%", maxWidth: "1250px", margin: "0 auto" }}>
            {pathname.includes("body-map") &&
              (pathname.includes("detail") ||
                pathname.includes("description") ||
                pathname.includes("edit")) && (
                <Box
                  sx={{
                    display: "flex",
                    background: "#fff",
                    zIndex: "998",
                    paddingLeft: "1rem",
                  }}
                >
                  <Box className="optionData" style={{ width: "fit-content" }}>
                    <Box onClick={handleBodyMapBack} className="bodymap-back">
                      <SVG.BackIcon width="20" />
                      {pathname.includes("edit") ? "Back" : "Back to body map"}
                    </Box>
                  </Box>
                </Box>
              )}

            {[
              "medication-list",
              "mar-chart",
              "medication-details",
              "discontinued-medication",
            ].includes(pathname?.split("/").at(-1) as string) && (
              <Box
                sx={{
                  display: "flex",
                  background: "#fff",
                  zIndex: "998",
                  paddingLeft: "1rem",
                }}
              >
                <Box className="optionData" style={{ width: "fit-content" }}>
                  <Link href={"/service/medication"} className="bodymap-back">
                    <SVG.BackIcon width="20" />
                    Back
                  </Link>
                </Box>
              </Box>
            )}

            {pathname.includes("/alert/") && (
              <Box
                sx={{
                  display: "flex",
                  background: "#fff",
                  zIndex: "998",
                  paddingLeft: "1rem",
                }}
              >
                <Box className="optionData" style={{ width: "fit-content" }}>
                  <Box
                    onClick={() => router.back()}
                    className="alert-back"
                    sx={{
                      color: "#3E73A4",
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                      justifyContent: "flex-start",
                      fontWeight: 500,
                      fontSize: 14,
                      cursor: "pointer",
                    }}
                  >
                    <SVG.BackIcon width="20" />
                    Back
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </HeaderStyled>
  );
}

export default Header;
